// Ops · Customers: pure view logic and the actions the page sends (docs/PHASE7-CONTRACT.md,
// contract.d.ts OpsCustomer / OpsCustomerDetail). No DOM, no Intl: node --test runs it.
import { count, peso } from '../../lib/format.js';
import { validateCredit } from '../express/lib.js';
import { manilaDate, manilaStamp } from '../reports/period.js';

/** @typedef {import('@popcart/core/v1').OpsCustomer} OpsCustomer */
/** @typedef {import('@popcart/core/v1').OpsCustomerDetail} OpsCustomerDetail */
/** @typedef {import('@popcart/core/v1').PopCartApi} PopCartApi */

export const customerKeys = {
  /** @param {string} q */
  list: (q) => ['ops', 'customers', q],
  /** @param {string} userId */
  detail: (userId) => ['ops', 'customer', userId],
};

/** Search waits this long after the last keystroke. */
export const SEARCH_DEBOUNCE_MS = 300;

/** The query the list sends: trimmed, and nothing at all when empty. @param {string} q */
export function customerParams(q) {
  const s = String(q ?? '').trim().replace(/\s+/g, ' ');
  return s ? { q: s } : {};
}

/** The name a person goes by here: name, else email, else phone. @param {Pick<OpsCustomer, 'name' | 'email' | 'phone'>} c */
export function customerTitle(c) {
  return c.name?.trim() || c.email || c.phone || 'No name yet';
}

/** "maria@x.ph · 0917 123 4567", only what's there. @param {Pick<OpsCustomer, 'name' | 'email' | 'phone'>} c */
export function contactLine(c) {
  const parts = [c.name?.trim() ? c.email : null, c.phone].filter(Boolean);
  return parts.join(' · ');
}

/**
 * One table row per customer.
 * @param {OpsCustomer} c
 */
export function customerRow(c) {
  return {
    id: c.userId,
    title: customerTitle(c),
    contact: contactLine(c),
    orders: count(c.ordersCount),
    delivered: c.ordersCount ? `${count(c.deliveredCount)} delivered` : 'no orders yet',
    spent: peso(c.spentCentavos),
    credit: c.creditBalanceCentavos > 0 ? peso(c.creditBalanceCentavos) : '—',
    hasCredit: c.creditBalanceCentavos > 0,
    lastOrder: c.lastOrderAt ? manilaDate(c.lastOrderAt) : 'never',
    joined: manilaDate(c.createdAt),
    deleted: c.deleted,
  };
}

/** @type {Record<string, { label: string; tone: 'success' | 'warning' | 'error' | 'info' | 'neutral' }>} */
export const STAGE_VIEW = {
  placed: { label: 'Placed', tone: 'info' },
  confirmed: { label: 'Confirmed', tone: 'info' },
  preparing: { label: 'Preparing', tone: 'warning' },
  ready: { label: 'Ready', tone: 'warning' },
  on_the_way: { label: 'On the way', tone: 'info' },
  arriving: { label: 'Arriving', tone: 'info' },
  delivered: { label: 'Delivered', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

/** @param {string} stage */
export function stageView(stage) {
  return STAGE_VIEW[stage] ?? { label: stage.replace(/_/g, ' '), tone: 'neutral' };
}

/** Still moving: the live board has it. @param {string} stage */
export function isLive(stage) {
  return stage !== 'delivered' && stage !== 'cancelled';
}

/**
 * A credit's state on `now`: usable, used up or expired.
 * @param {OpsCustomerDetail['credits'][number]} c
 * @param {number} now ms epoch
 */
export function creditView(c, now) {
  const reason = c.reason === 'express_late' ? 'Express late' : 'Goodwill';
  if (c.remainingCentavos <= 0) return { reason, state: 'Used', tone: /** @type {const} */ ('neutral'), until: manilaDate(c.expiresAt) };
  if (Date.parse(c.expiresAt) <= now) return { reason, state: 'Expired', tone: /** @type {const} */ ('neutral'), until: manilaDate(c.expiresAt) };
  return { reason, state: `${peso(c.remainingCentavos)} left`, tone: /** @type {const} */ ('success'), until: manilaDate(c.expiresAt) };
}

/** "Joined 3 Jan · last order 6 Oct, 14:05". @param {OpsCustomer} c */
export function customerSince(c) {
  return `Joined ${manilaDate(c.createdAt)} · ${c.lastOrderAt ? `last order ${manilaStamp(c.lastOrderAt)}` : 'no orders yet'}`;
}

/**
 * "Give a credit": validate the form, then the Phase 6 goodwill endpoint for this customer.
 * @param {Pick<PopCartApi, 'issueCredit'>} api
 * @param {string} userId
 * @param {{ amount: string; note: string }} form
 */
export async function giveCredit(api, userId, form) {
  const r = validateCredit(form);
  if (!r.ok) return r;
  const credit = await api.issueCredit({ userId, amountCentavos: r.value.amountCentavos, note: r.value.note });
  return { ok: /** @type {const} */ (true), credit };
}
